import { useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/en';

dayjs.locale('en');

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

interface DateRangeCalendarProps {
  startDate: string;
  endDate: string;
  onChange: (range: DateRangeValue) => void;
  className?: string;
}

function parseIso(value: string): Dayjs | null {
  if (!value || !dayjs(value).isValid()) return null;
  return dayjs(value).startOf('day');
}

export function DateRangeCalendar({
  startDate,
  endDate,
  onChange,
  className = '',
}: DateRangeCalendarProps) {
  const start = parseIso(startDate);
  const end = parseIso(endDate);
  const anchor = end ?? start ?? dayjs();
  const [viewMonth, setViewMonth] = useState(() => anchor.startOf('month'));

  useEffect(() => {
    const next = end ?? start;
    if (next) setViewMonth(next.startOf('month'));
  }, [startDate, endDate]);

  const monthCells = useMemo(() => {
    const offset = viewMonth.day();
    const total = viewMonth.daysInMonth();
    const cells: Array<number | null> = [];
    for (let i = 0; i < offset; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push(d);
    return cells;
  }, [viewMonth]);

  const handleDayClick = (day: number) => {
    const picked = viewMonth.date(day).startOf('day');
    const iso = picked.format('YYYY-MM-DD');

    // No start yet, or both ends already set → begin a new range
    if (!start || (start && end)) {
      onChange({ startDate: iso, endDate: '' });
      return;
    }

    // Have start, picking end
    if (picked.isBefore(start, 'day')) {
      onChange({ startDate: iso, endDate: start.format('YYYY-MM-DD') });
      return;
    }
    onChange({ startDate: start.format('YYYY-MM-DD'), endDate: iso });
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setViewMonth((prev) => prev.subtract(1, 'month').startOf('month'))}
          className="w-7 h-7 rounded-md border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
          aria-label="Previous month"
        >
          <i className="ri-arrow-left-s-line text-gray-600 text-base" />
        </button>
        <div className="text-gray-800 text-xs font-semibold">{viewMonth.format('MMMM YYYY')}</div>
        <button
          type="button"
          onClick={() => setViewMonth((prev) => prev.add(1, 'month').startOf('month'))}
          className="w-7 h-7 rounded-md border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
          aria-label="Next month"
        >
          <i className="ri-arrow-right-s-line text-gray-600 text-base" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="text-center text-[10px] font-semibold text-gray-400 py-1">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {monthCells.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="h-8" />;
          }

          const current = viewMonth.date(day).startOf('day');
          const iso = current.format('YYYY-MM-DD');
          const isStart = Boolean(start && current.isSame(start, 'day'));
          const isEnd = Boolean(end && current.isSame(end, 'day'));
          const inRange = Boolean(
            start && end && !current.isBefore(start, 'day') && !current.isAfter(end, 'day'),
          );
          const isToday = current.isSame(dayjs(), 'day');
          const isEndpoint = isStart || isEnd;

          return (
            <button
              key={iso}
              type="button"
              onClick={() => handleDayClick(day)}
              className={`relative h-8 text-xs font-medium transition-colors cursor-pointer ${
                isEndpoint
                  ? 'bg-green-600 text-white rounded-md z-[1]'
                  : inRange
                    ? 'bg-green-50 text-green-800 rounded-none'
                    : isToday
                      ? 'text-green-700 hover:bg-green-50 rounded-md'
                      : 'text-gray-700 hover:bg-gray-50 rounded-md'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>

      <div className="mt-3 border-t border-gray-100" />
    </div>
  );
}
